import React, { createContext, useContext } from "react"
import { MdClose } from "react-icons/md"
import {
	MobergButton,
	MobergButtonShape,
	MobergCheckbox,
	MobergDropdown,
	MobergIconSize,
	MobergNumberInput,
	MobergRow,
	MobergShortTextInput,
} from "../../../../../Moberg"
import { COLOR_SPECTRUM_OPTIONS, ColorSpectrum } from "../../../../../Pages/Data/Visualize/DataReview/Types/ColorSpectrum"
import { DSADisplayScale } from "../../../../../Pages/Data/Visualize/DataReview/Types/AnalysisDetails"
import { SpectrogramTraceConfigJSON, TraceOption, UpdateTraceOptions } from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { SpectrumSample } from "../SpectrumSample"

export const MAX_SPECTROGRAM_TRACES = 10

export const DISPLAY_SCALE_OPTIONS: Array<{ label: string; value: DSADisplayScale }> = [
	{ label: "Power (µV²)", value: "power" },
	{ label: "Voltage (µV)", value: "voltage" },
	{ label: "SQRT Voltage (√µV)", value: "sqrt_voltage" },
]

type SpectrogramGraphModalitiesContextValue = {
	activeSourceSpectrogramTraces: SpectrogramTraceConfigJSON[]
	activeAnalyticSpectrogramTraces: SpectrogramTraceConfigJSON[]
	handleColorSpectrumChange: (colorSpectrum: ColorSpectrum, traceConfig: SpectrogramTraceConfigJSON) => void
	handleValidationErrorChange: (key: string, error: string | undefined) => void
	handleDelete: (id: string) => void
	updateTrace: (traceId: string, config: Partial<SpectrogramTraceConfigJSON>, options?: UpdateTraceOptions) => void
}

export const SpectrogramGraphModalitiesContext = createContext<SpectrogramGraphModalitiesContextValue>({} as SpectrogramGraphModalitiesContextValue)

export const useReasonAddSpectrogramTraceIsDisabled = (options: TraceOption[], noOptionsMessage: string): string | undefined => {
	const { activeSourceSpectrogramTraces, activeAnalyticSpectrogramTraces } = useContext(SpectrogramGraphModalitiesContext)

	// The render cap protects the shared canvas pipeline, so it counts every
	// spectrogram trace on the graph, source and analytic alike.
	const totalSpectrogramTraces = activeSourceSpectrogramTraces.length + activeAnalyticSpectrogramTraces.length

	if (totalSpectrogramTraces >= MAX_SPECTROGRAM_TRACES) {
		return `There cannot be more than ${MAX_SPECTROGRAM_TRACES} spectrogram traces in a graph.`
	}

	return options.length === 0 ? noOptionsMessage : undefined
}

// The renderer normalizes cell values by (upperBound - lowerBound), so equal or
// reversed bounds must be rejected before they can be saved.
const validateLowerBound = (trace: SpectrogramTraceConfigJSON) => (value: number): string | undefined => {
	if (!Number.isFinite(value)) {
		return "Lower bound must be a real number."
	}

	if (value >= trace.upperBound) {
		return "Lower bound must be less than the upper bound."
	}

	return undefined
}

const validateUpperBound = (trace: SpectrogramTraceConfigJSON) => (value: number): string | undefined => {
	if (!Number.isFinite(value)) {
		return "Upper bound must be a real number."
	}

	if (value <= trace.lowerBound) {
		return "Upper bound must be greater than the lower bound."
	}

	return undefined
}

type SpectrogramTraceSettingsCellsProps = {
	trace: SpectrogramTraceConfigJSON
	testIdPrefix: "spectrogram" | "spectrogram-source"
	labelInputWidth?: string
	labelCharacterLimit?: number
	// The analytic tab rebuilds the trace's data key when the scale changes;
	// without an override the scale is stored directly on the trace.
	onDisplayScaleChange?: (displayScale: DSADisplayScale) => void
}

// The settings cells both tabs share: label, display scale, color spectrum,
// bounds, visibility, delete. Rendered as a fragment so each tab's grid
// supplies its own leading picker cells and column template.
export const SpectrogramTraceSettingsCells: React.FC<SpectrogramTraceSettingsCellsProps> = ({
	trace,
	testIdPrefix,
	labelInputWidth = "140px",
	labelCharacterLimit = 24,
	onDisplayScaleChange,
}) => {
	const { handleColorSpectrumChange, handleValidationErrorChange, handleDelete, updateTrace } = useContext(SpectrogramGraphModalitiesContext)

	const handleBoundChange = (bound: "lowerBound" | "upperBound", value: number | null) => {
		updateTrace(trace.id, { [bound]: value ?? 0 })
	}

	return (
		<>
			<MobergShortTextInput
				value={trace.name}
				defaultValue={trace.name}
				onChange={name => updateTrace(trace.id, { name })}
				style={{ width: labelInputWidth }}
				limit={labelCharacterLimit}
			/>

			<MobergDropdown
				dataTestId={`${testIdPrefix}-display-scale-select`}
				options={DISPLAY_SCALE_OPTIONS}
				selectedValue={trace.displayScale}
				onChange={displayScale => (onDisplayScaleChange ? onDisplayScaleChange(displayScale) : updateTrace(trace.id, { displayScale }))}
				width={175}
			/>

			<MobergDropdown
				dataTestId={`${testIdPrefix}-color-spectrum-select`}
				selectedValue={trace.colorSpectrum}
				onChange={spectrum => handleColorSpectrumChange(spectrum, trace)}
				options={COLOR_SPECTRUM_OPTIONS}
				width={175}
			/>

			<MobergNumberInput
				defaultValue={trace.lowerBound}
				value={trace.lowerBound}
				onChange={value => handleBoundChange("lowerBound", value)}
				onValidationErrorChange={error => handleValidationErrorChange(`${trace.id}-lowerBound`, error)}
				validationFunction={validateLowerBound(trace)}
				style={{ width: "100%" }}
			/>

			<MobergRow>
				<SpectrumSample colorSpectrum={trace.colorSpectrum} />
			</MobergRow>

			<MobergNumberInput
				defaultValue={trace.upperBound}
				value={trace.upperBound}
				onChange={value => handleBoundChange("upperBound", value)}
				onValidationErrorChange={error => handleValidationErrorChange(`${trace.id}-upperBound`, error)}
				validationFunction={validateUpperBound(trace)}
				style={{ width: "100%" }}
			/>

			<MobergCheckbox isChecked={trace.isVisible ?? true} onChange={isVisible => updateTrace(trace.id, { isVisible })} />

			<MobergButton dataTestId={`${testIdPrefix}-trace-delete-button`} shape={MobergButtonShape.SQUARE} onClick={() => handleDelete(trace.id)} style={{ padding: "6px" }}>
				<MdClose size={MobergIconSize.REGULAR} />
			</MobergButton>
		</>
	)
}
